import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, RefreshCw, Users, CalendarDays, Clock, Banknote, Eye, Inbox, Check, Archive, Download, Paperclip, Trash2, CalendarCheck, FileText, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const API = `${backendUrl}/api`;

const typeLabel = (t) => ({
  paid_leave: 'Ücretli', unpaid_leave: 'Ücretsiz', weekday: 'Gün İçi', holiday: 'Resmi Tatil',
  is_late: 'İşe Geç Geldi', present: 'İşe Geldi', half_day: 'Yarım Gün', absent: 'Gelmedi',
  sick: 'Raporlu', annual: 'Yıllık İzin', weekend: 'Hafta Sonu', holiday_overtime: 'Tatil Mesaisi',
  kimlik: 'Kimlik', ehliyet: 'Ehliyet', ikametgah: 'İkametgah', sabika: 'Sabıka Kaydı',
  diploma: 'Diploma', oda_kaydi: 'Oda Kaydı', saglik_raporu: 'Sağlık Raporu', cv: 'CV',
  ise_giris_bildirgesi: 'İşe Giriş Bildirgesi', aile_durum_bildirimi: 'Aile Durum Bildirimi',
  is_sozlesmesi: 'İş Sözleşmesi', kvkk: 'KVKK Belgesi', aydinlatma_metni: 'Aydınlatma Metni',
  gorev_tanim: 'Görev Tanım Belgesi', isg_egitim: 'İSG Eğitim Belgeleri',
}[t] || t);

const CATEGORIES = [
  {
    key: 'izin', label: 'İzin Uyarıları', endpoint: `${API}/hr/warnings/izin`, icon: CalendarDays,
    typesField: 'leave_types', totalLabel: 'Toplam İzin (gün)', thresholdSuffix: 'günden fazla',
    col1: { label: 'Ücretli', field: 'paid_leave_days' }, col2: { label: 'Ücretsiz', field: 'unpaid_leave_days' },
  },
  {
    key: 'mesai', label: 'Mesai Uyarıları', endpoint: `${API}/hr/warnings/mesai`, icon: CalendarCheck,
    typesField: 'attendance_statuses', totalLabel: 'Toplam (gün)', thresholdSuffix: 'günden fazla',
    col1: { label: 'İşe Geç Geldi', field: 'late_days' }, col2: { label: 'Gelmedi', field: 'absent_days' },
  },
  {
    key: 'belge', label: 'Belge Uyarıları', endpoint: `${API}/hr/warnings/belge`, icon: FileText,
    typesField: 'document_types', totalLabel: 'Eksik Belge', thresholdSuffix: 'gün sonra eksikse',
    col1: { label: 'Eksik Belgeler', field: 'missing_text' }, col2: { label: 'Kıdem (gün)', field: 'days_since_hire' },
  },
  {
    key: 'maas', label: 'Maaş Uyarıları', endpoint: `${API}/hr/warnings/maas`, icon: Wallet,
    typesField: null, totalLabel: 'Değişmeyen Süre (ay)', thresholdSuffix: 'aydır değişmemiş',
    col1: { label: 'Güncel Maaş (₺)', field: 'current_salary' }, col2: { label: 'Son Değişiklik', field: 'last_change_date' },
  },
  {
    key: 'fazla_mesai', label: 'Fazla Mesai Uyarıları', endpoint: `${API}/hr/warnings/fazla-mesai`, icon: Clock,
    typesField: 'overtime_types', totalLabel: 'Toplam Saat', thresholdSuffix: 'saatten fazla',
    col1: { label: 'Gün İçi', field: 'weekday_hours' }, col2: { label: 'Tatil', field: 'holiday_hours' },
  },
  {
    key: 'avans', label: 'Avans Uyarıları', endpoint: `${API}/hr/warnings/avans`, icon: Banknote,
    typesField: null, totalLabel: 'Toplam Avans (₺)', thresholdSuffix: '₺ üzeri',
    col1: { label: 'Avans Ayı', field: 'advance_months' }, col2: { label: 'En Yüksek (₺)', field: 'max_advance' },
  },
];

const HRWarnings = () => {
  const [warningsByCat, setWarningsByCat] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active'); // 'active' | 'processing' | 'archive'
  const [archiveList, setArchiveList] = useState([]);
  const [archiveModal, setArchiveModal] = useState({ open: false, catKey: null, rule: null, match: null });
  const [archiveNote, setArchiveNote] = useState('');
  const [archiveFile, setArchiveFile] = useState(null);
  const [savingArchive, setSavingArchive] = useState(false);

  const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('fortis_token')}` } });

  const fetchWarnings = useCallback(async () => {
    setLoading(true);
    try {
      const entries = await Promise.all(CATEGORIES.map(async (c) => {
        try { const r = await axios.get(c.endpoint, authH()); return [c.key, r.data || []]; }
        catch (e) { return [c.key, []]; }
      }));
      setWarningsByCat(Object.fromEntries(entries));
    } finally { setLoading(false); }
  }, []);

  const fetchArchive = useCallback(async () => {
    try { const r = await axios.get(`${API}/hr/warnings/archive`, authH()); setArchiveList(r.data || []); }
    catch (e) { setArchiveList([]); }
  }, []);

  useEffect(() => { fetchWarnings(); fetchArchive(); }, [fetchWarnings, fetchArchive]);

  const setAction = async (catKey, rule, match, status) => {
    try {
      await axios.post(`${API}/hr/warning-actions`, {
        rule_type: catKey, rule_id: rule.rule_id, employee_id: match.employee_id, status, count: match.leave_days,
      }, authH());
      if (status === 'processing') toast.success('İşleme alındı');
      else if (tab === 'active') toast.success('Görüldü olarak işaretlendi');
      else toast.success('Kaldırıldı');
      fetchWarnings();
    } catch (e) { toast.error('İşlem yapılamadı'); }
  };

  const openArchiveModal = (catKey, rule, match) => {
    setArchiveModal({ open: true, catKey, rule, match });
    setArchiveNote(''); setArchiveFile(null);
  };

  const onArchiveFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error('Dosya 10MB üzerinde olamaz'); return; }
    const reader = new FileReader();
    reader.onload = () => setArchiveFile({ name: f.name, type: f.type, data: String(reader.result).split(',')[1] || '' });
    reader.readAsDataURL(f);
  };

  const submitArchive = async () => {
    const { catKey, rule, match } = archiveModal;
    if (!rule || !match) return;
    const cat = CATEGORIES.find((c) => c.key === catKey);
    setSavingArchive(true);
    try {
      await axios.post(`${API}/hr/warnings/archive`, {
        rule_type: catKey, rule_id: rule.rule_id, employee_id: match.employee_id,
        employee_name: match.name, firm_name: match.firm_name, branch_name: match.branch_name,
        rule_title: rule.title, period_label: rule.period_label,
        leave_types: rule[cat.typesField] || [], leave_days: match.leave_days,
        note: archiveNote.trim(), file: archiveFile,
      }, authH());
      toast.success('Arşive eklendi');
      setArchiveModal({ open: false, catKey: null, rule: null, match: null });
      fetchWarnings(); fetchArchive();
    } catch (e) { toast.error('Arşive eklenemedi'); }
    finally { setSavingArchive(false); }
  };

  const downloadArchiveFile = async (a) => {
    try {
      const res = await axios.get(`${API}/hr/warnings/archive/${a.id}/file`, authH());
      const f = res.data;
      const link = document.createElement('a');
      link.href = f.file_url || f.url || (f.data ? `data:${f.type || 'application/octet-stream'};base64,${f.data}` : '');
      link.download = f.name || 'belge';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch (e) { toast.error('Belge indirilemedi'); }
  };

  const deleteArchive = async (id) => {
    try { await axios.delete(`${API}/hr/warnings/archive/${id}`, authH()); toast.success('Arşiv kaydı silindi'); fetchArchive(); }
    catch (e) { toast.error('Silinemedi'); }
  };

  const allRules = CATEGORIES.flatMap((c) => (warningsByCat[c.key] || []));
  const totalActive = allRules.reduce((s, r) => s + (r.match_count || 0), 0);
  const totalProcessing = allRules.reduce((s, r) => s + (r.processing_count || 0), 0);
  const anyRulesDefined = allRules.length > 0;

  // Build sections per category with filtered matches for current tab
  const sections = CATEGORIES.map((cat) => ({
    cat,
    rules: (warningsByCat[cat.key] || []).map((rule) => ({
      ...rule,
      filtered: (rule.matches || []).filter((m) => tab === 'active' ? m.action_status === 'active' : m.action_status === 'processing'),
    })).filter((rule) => rule.filtered.length > 0),
  })).filter((s) => s.rules.length > 0);

  return (
    <div className="space-y-6" data-testid="hr-warnings-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-100"><AlertTriangle className="h-6 w-6 text-amber-600" /></div>
          <div>
            <h1 className="text-2xl font-bold">Uyarılar</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Kurallar bölümünde tanımlanan uyarı kurallarının sonuçları</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => { fetchWarnings(); fetchArchive(); }} disabled={loading} data-testid="warnings-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Yenile
        </Button>
      </div>

      <div className="flex gap-2 border-b border-[hsl(var(--border))]">
        <button type="button" onClick={() => setTab('active')} data-testid="warnings-tab-active"
          className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors flex items-center gap-2 ${tab === 'active' ? 'bg-amber-600 text-white' : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]'}`}>
          <AlertTriangle className="h-4 w-4" /> Aktif Uyarılar
          <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${tab === 'active' ? 'bg-white/25' : 'bg-red-100 text-red-700'}`}>{totalActive}</span>
        </button>
        <button type="button" onClick={() => setTab('processing')} data-testid="warnings-tab-processing"
          className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors flex items-center gap-2 ${tab === 'processing' ? 'bg-indigo-600 text-white' : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]'}`}>
          <Inbox className="h-4 w-4" /> İşleme Alınanlar
          <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${tab === 'processing' ? 'bg-white/25' : 'bg-indigo-100 text-indigo-700'}`}>{totalProcessing}</span>
        </button>
        <button type="button" onClick={() => setTab('archive')} data-testid="warnings-tab-archive"
          className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors flex items-center gap-2 ${tab === 'archive' ? 'bg-slate-700 text-white' : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]'}`}>
          <Archive className="h-4 w-4" /> Arşiv
          <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${tab === 'archive' ? 'bg-white/25' : 'bg-slate-200 text-slate-700'}`}>{archiveList.length}</span>
        </button>
      </div>

      {tab === 'archive' ? (
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Archive className="h-5 w-5 text-slate-700" /> Arşiv</CardTitle></CardHeader>
          <CardContent>
            {archiveList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="archive-empty">
                <Archive className="h-10 w-10 mb-3 text-slate-300" /><p>Arşivde kayıt yok.</p>
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="archive-table">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Çalışan</TableHead><TableHead>Kural</TableHead><TableHead className="text-center">Değer</TableHead>
                    <TableHead>Açıklama</TableHead><TableHead>Belge</TableHead><TableHead>Tarih</TableHead><TableHead className="text-right">İşlem</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {archiveList.map((a) => (
                      <TableRow key={a.id} data-testid={`archive-row-${a.id}`}>
                        <TableCell className="font-medium">{a.employee_name}<div className="text-xs text-[hsl(var(--muted-foreground))]">{a.firm_name} · {a.branch_name}</div></TableCell>
                        <TableCell className="text-sm">{a.rule_title}<div className="text-xs text-[hsl(var(--muted-foreground))]">{a.rule_type === 'fazla_mesai' ? 'Fazla Mesai' : a.rule_type === 'mesai' ? 'Mesai' : a.rule_type === 'avans' ? 'Avans' : a.rule_type === 'belge' ? 'Belge' : a.rule_type === 'maas' ? 'Maaş' : 'İzin'} · {a.period_label}</div></TableCell>
                        <TableCell className="text-center font-bold text-red-600">{a.leave_days}</TableCell>
                        <TableCell className="text-sm max-w-xs whitespace-pre-wrap">{a.note || '-'}</TableCell>
                        <TableCell>
                          {a.has_file ? (
                            <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" onClick={() => downloadArchiveFile(a)} data-testid={`archive-file-${a.id}`}>
                              <Download className="h-4 w-4 mr-1" /> {a.file_name || 'İndir'}
                            </Button>
                          ) : <span className="text-xs text-[hsl(var(--muted-foreground))]">-</span>}
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{a.archived_at ? new Date(a.archived_at).toLocaleDateString('tr-TR') : '-'}<div className="text-xs text-[hsl(var(--muted-foreground))]">{a.archived_by_name}</div></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteArchive(a.id)} data-testid={`archive-delete-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : loading ? (
        <Card><CardContent><div className="text-center py-10 text-[hsl(var(--muted-foreground))]">Yükleniyor...</div></CardContent></Card>
      ) : !anyRulesDefined ? (
        <Card><CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center text-[hsl(var(--muted-foreground))]">
            <AlertTriangle className="h-10 w-10 mb-3 text-amber-400" />
            <p>Henüz uyarı kuralı tanımlanmamış.</p>
            <p className="text-xs mt-1">İK &gt; Kurallar &gt; Uyarı Kuralları'ndan kural ekleyin.</p>
          </div>
        </CardContent></Card>
      ) : sections.length === 0 ? (
        <Card><CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center text-[hsl(var(--muted-foreground))]">
            {tab === 'active' ? <Check className="h-10 w-10 mb-3 text-green-400" /> : <Inbox className="h-10 w-10 mb-3 text-indigo-300" />}
            <p>{tab === 'active' ? 'Aktif uyarı yok. Tümü görüldü veya işleme alındı.' : 'İşleme alınan uyarı yok.'}</p>
          </div>
        </CardContent></Card>
      ) : (
        sections.map(({ cat, rules }) => {
          const CatIcon = cat.icon;
          return (
            <Card key={cat.key} data-testid={`warnings-section-${cat.key}`}>
              <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><CatIcon className="h-5 w-5 text-amber-600" /> {cat.label}</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {rules.map((rule) => (
                    <div key={rule.rule_id} data-testid={`warn-rule-${rule.rule_id}`} className="border rounded-xl overflow-hidden">
                      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-amber-50 border-b border-amber-100">
                        <div>
                          <div className="font-semibold text-gray-900">{rule.title}</div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                            {cat.typesField && (rule[cat.typesField] || []).length > 0 && (
                              <>{(rule[cat.typesField] || []).map(typeLabel).join(', ')}{' · '}</>
                            )}
                            {rule.period_label}{' · '}{rule.threshold_days} {cat.thresholdSuffix}
                          </div>
                        </div>
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
                          <Users className="h-4 w-4" /> {rule.filtered.length} kişi
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader><TableRow>
                            <TableHead>Çalışan</TableHead><TableHead>Firma</TableHead><TableHead>Şube</TableHead>
                            <TableHead className="text-center">{cat.totalLabel}</TableHead>
                            <TableHead className="text-center">{cat.col1.label}</TableHead>
                            <TableHead className="text-center">{cat.col2.label}</TableHead>
                            <TableHead className="text-right">İşlem</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>
                            {rule.filtered.map((m) => (
                              <TableRow key={m.employee_id} data-testid={`warn-match-${m.employee_id}`}>
                                <TableCell className="font-medium">{m.name}{m.is_terminated && <span className="ml-2 text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600">Ayrılmış</span>}</TableCell>
                                <TableCell>{m.firm_name}</TableCell>
                                <TableCell>{m.branch_name}</TableCell>
                                <TableCell className="text-center font-bold text-red-600">{m.leave_days}</TableCell>
                                <TableCell className="text-center">{m[cat.col1.field] || '-'}</TableCell>
                                <TableCell className="text-center">{m[cat.col2.field] || '-'}</TableCell>
                                <TableCell className="text-right">
                                  {tab === 'active' ? (
                                    <div className="flex items-center justify-end gap-1">
                                      <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700 hover:bg-green-50" onClick={() => setAction(cat.key, rule, m, 'seen')} data-testid={`warn-seen-${m.employee_id}`}><Eye className="h-4 w-4 mr-1" /> Görüldü</Button>
                                      <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" onClick={() => setAction(cat.key, rule, m, 'processing')} data-testid={`warn-process-${m.employee_id}`}><Inbox className="h-4 w-4 mr-1" /> İşleme Al</Button>
                                    </div>
                                  ) : (
                                    <Button variant="ghost" size="sm" className="text-slate-700 hover:text-slate-900 hover:bg-slate-100" onClick={() => openArchiveModal(cat.key, rule, m)} data-testid={`warn-archive-${m.employee_id}`}><Archive className="h-4 w-4 mr-1" /> Arşive Ekle</Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Arşive Ekle Modal */}
      <Dialog open={archiveModal.open} onOpenChange={(o) => setArchiveModal((p) => ({ ...p, open: o }))}>
        <DialogContent className="max-w-md" data-testid="archive-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Archive className="h-5 w-5 text-slate-700" /> Arşive Ekle</DialogTitle>
            <DialogDescription>{archiveModal.match ? `${archiveModal.match.name} — ${archiveModal.match.leave_days}` : ''} kaydını açıklama ve belge ile arşive ekleyin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Açıklama</Label>
              <Textarea value={archiveNote} onChange={(e) => setArchiveNote(e.target.value)} placeholder="örn. Çalışanla görüşüldü, yazılı uyarı verildi..." rows={4} data-testid="archive-note" />
            </div>
            <div>
              <Label>Belge (opsiyonel)</Label>
              <input type="file" onChange={onArchiveFile} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 mt-1" data-testid="archive-file-input" />
              {archiveFile && <div className="flex items-center gap-1 text-xs text-green-700 mt-1"><Paperclip className="h-3 w-3" /> {archiveFile.name}</div>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveModal({ open: false, catKey: null, rule: null, match: null })}>İptal</Button>
            <Button onClick={submitArchive} disabled={savingArchive} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="archive-submit">
              <Archive className="h-4 w-4 mr-2" />{savingArchive ? 'Ekleniyor...' : 'Arşive Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRWarnings;
